/**
 * Daily Task Service
 * Daily task check-in system with streak rewards
 */

export interface DailyTask {
  id: string;
  type: 'vocabulary' | 'speaking' | 'listening' | 'game' | 'ai-chat';
  title: string;
  description: string;
  target: number; // target count
  current: number; // current progress
  reward: number; // base points reward
  completed: boolean;
  icon: string;
}

export interface DailyTaskProgress {
  date: string; // YYYY-MM-DD
  tasks: DailyTask[];
  streakDays: number;
  lastCheckIn: string;
  totalCompleted: number;
}

const STORAGE_KEY = 'english_tutor_daily_tasks';

const DEFAULT_TASKS: Omit<DailyTask, 'current' | 'completed'>[] = [
  {
    id: 'vocab-5',
    type: 'vocabulary',
    title: '学习单词',
    description: '学习5个新单词',
    target: 5,
    reward: 15,
    icon: '📚',
  },
  {
    id: 'speaking-3',
    type: 'speaking',
    title: '口语练习',
    description: '完成3次口语练习',
    target: 3,
    reward: 20,
    icon: '🎤',
  },
  {
    id: 'listening-2',
    type: 'listening',
    title: '听力训练',
    description: '完成2次听力练习',
    target: 2,
    reward: 15,
    icon: '🎧',
  },
  {
    id: 'game-1',
    type: 'game',
    title: '玩游戏',
    description: '玩1个学习游戏',
    target: 1,
    reward: 10,
    icon: '🎮',
  },
];

export class DailyTaskService {
  private userId: string;

  constructor(userId: string) {
    this.userId = userId;
  }

  // Get today's date string
  private getTodayString(): string {
    return new Date().toISOString().split('T')[0];
  }

  // Load daily task progress
  private loadProgress(): DailyTaskProgress {
    const key = `${STORAGE_KEY}_${this.userId}`;
    const data = localStorage.getItem(key);

    if (data) {
      return JSON.parse(data);
    }

    // Initialize new progress
    return {
      date: this.getTodayString(),
      tasks: DEFAULT_TASKS.map(t => ({
        ...t,
        current: 0,
        completed: false,
      })),
      streakDays: 0,
      lastCheckIn: '',
      totalCompleted: 0,
    };
  }

  // Save daily task progress
  private saveProgress(progress: DailyTaskProgress): void {
    const key = `${STORAGE_KEY}_${this.userId}`;
    localStorage.setItem(key, JSON.stringify(progress));
  }

  // Get today's tasks
  getTodayTasks(): DailyTaskProgress {
    const progress = this.loadProgress();
    const today = this.getTodayString();

    // Reset tasks if it's a new day
    if (progress.date !== today) {
      // Update streak
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayString = yesterday.toISOString().split('T')[0];

      if (progress.lastCheckIn === yesterdayString) {
        progress.streakDays += 1;
      } else if (progress.lastCheckIn !== today) {
        // Streak broken
        progress.streakDays = 0;
      }

      // Reset tasks for new day
      progress.date = today;
      progress.tasks = DEFAULT_TASKS.map(t => ({
        ...t,
        current: 0,
        completed: false,
      }));
    }

    this.saveProgress(progress);
    return progress;
  }

  // Update task progress
  updateTaskProgress(taskType: DailyTask['type'], increment: number = 1): DailyTask {
    const progress = this.getTodayTasks();

    const task = progress.tasks.find(t => t.type === taskType);
    if (!task) return task!;

    // Update current progress
    task.current = Math.min(task.current + increment, task.target);

    // Check if completed
    if (task.current >= task.target && !task.completed) {
      task.completed = true;
      progress.totalCompleted += 1;
      progress.lastCheckIn = this.getTodayString();
    }

    this.saveProgress(progress);
    return task;
  }

  // Calculate streak bonus
  calculateStreakBonus(): number {
    const progress = this.getTodayTasks();

    // Base multiplier
    if (progress.streakDays >= 7) return 1.5; // 50% bonus
    if (progress.streakDays >= 3) return 1.2; // 20% bonus
    return 1.0;
  }

  // Get total reward for completed tasks today
  getTodayReward(): number {
    const progress = this.getTodayTasks();
    const baseReward = progress.tasks
      .filter(t => t.completed)
      .reduce((sum, t) => sum + t.reward, 0);

    const streakBonus = this.calculateStreakBonus();
    return Math.round(baseReward * streakBonus);
  }

  // Check if all tasks completed
  isAllTasksCompleted(): boolean {
    const progress = this.getTodayTasks();
    return progress.tasks.every(t => t.completed);
  }

  // Use streak protection (spend points to keep streak)
  useStreakProtection(): boolean {
    const progress = this.loadProgress();

    // Cost: 50 points
    if (progress.streakDays >= 2) {
      progress.streakDays -= 1;
      this.saveProgress(progress);
      return true;
    }

    return false;
  }

  // Get streak message
  getStreakMessage(): string {
    const progress = this.getTodayTasks();

    if (progress.streakDays === 0) {
      return '开始打卡吧！连续学习获得额外奖励 🎯';
    } else if (progress.streakDays < 3) {
      return `已连续学习 ${progress.streakDays} 天！坚持到3天获得20%奖励加成 💪`;
    } else if (progress.streakDays < 7) {
      return `太棒了！连续 ${progress.streakDays} 天！坚持到7天获得50%奖励加成 🌟`;
    } else {
      return `厉害！连续 ${progress.streakDays} 天！你已获得50%奖励加成 🏆`;
    }
  }

  // Get completion percentage
  getCompletionPercentage(): number {
    const progress = this.getTodayTasks();
    const completed = progress.tasks.filter(t => t.completed).length;
    return Math.round((completed / progress.tasks.length) * 100);
  }
}

// Singleton instance (will be created with user ID)
export let dailyTaskService: DailyTaskService | null = null;

export function initDailyTaskService(userId: string): void {
  dailyTaskService = new DailyTaskService(userId);
}